/**
 * Resolve a notification doc (template_key + params) into rendered { title, body,
 * ctaLabel } strings for the active vue-i18n locale.
 *
 * The composable is the single place where pluralisation, parameter formatting,
 * and unknown-template fallback live. Components stay dumb — they just call
 * `useNotificationStrings(notification)` and bind the result to the template.
 */

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { normalizeNotificationRouteTarget } from '@/features/notifications/domain/deepLinks';
import {
  formatDate,
  formatDuration,
  formatNumber,
  formatRelativeTime,
  resolveLocale,
} from '@/i18n/formattersCore';

function _safeTe(i18n) {
  return typeof i18n.te === 'function' ? i18n.te : () => true;
}

function _toRef(value) {
  return typeof value === 'function' ? value : () => value;
}

function _applyLocalizedParams(params, locale) {
  const localeKey = String(locale || '')
    .split('-')[0]
    .toLowerCase();
  for (const [key, value] of Object.entries(params)) {
    if (!key.endsWith('_i18n') || !value || typeof value !== 'object') continue;
    const targetKey = key.slice(0, -'_i18n'.length);
    const selected = value[localeKey] || value[locale] || value.en || value.nl;
    if (typeof selected === 'string') {
      params[targetKey] = selected;
    }
  }
  return params;
}

function _nonBlank(value) {
  return typeof value === 'string' && value.trim() !== '';
}

// Minute-granular sibling of `formatDuration` (same terse unit letters), for the
// length of an alert episode: "45 min", "2u 15m", "2d 2u".
function _formatMinutes(minutes, locale) {
  const total = Math.max(0, Math.floor(minutes));
  const isNl = resolveLocale(locale) === 'nl';
  const hourUnit = isNl ? 'u' : 'h';
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  if (d > 0) return `${d}d ${h}${hourUnit}`;
  if (h > 0) return `${h}${hourUnit} ${m}m`;
  return `${m} min`;
}

/**
 * Derived params for the `alert.*` templates. Each is only set when the worker
 * sent something usable, because `ALERT_VARIANTS` below picks the message keys
 * by their presence — a missing string is a sentence left out, never a gap.
 *
 * - `presented`: what `alert.triggered` says, from the worker's presenter
 *   strings (`headline`, else `condition_sentence` + `value_display`; then
 *   `value_context` and `timestamp_display`), already overlaid with their
 *   `_i18n` entry for the UI locale. Absent on notifications from before the
 *   presenter → today's body.
 * - `duration` / `peak` / `condition`: length, most extreme value and condition
 *   sentence of a cleared episode.
 * - `episode_since`: when the episode a reminder belongs to began.
 * - `since_display`: the moment an unhealthy/recovered notice refers to.
 */
function _applyAlertParams(params, locale) {
  const main = _nonBlank(params.headline)
    ? params.headline
    : [params.condition_sentence, params.value_display].filter(_nonBlank).join(': ');
  if (_nonBlank(main)) {
    params.presented = [main, params.value_context, params.timestamp_display]
      .filter(_nonBlank)
      .map((part) => part.trim())
      .join(' · ');
  }
  if (typeof params.duration_minutes === 'number' && Number.isFinite(params.duration_minutes)) {
    params.duration = _formatMinutes(params.duration_minutes, locale);
  }
  // The worker's `peak_display` knows what kind of value the condition is about
  // (a count is not a price). When the worker sends the field at all, it decides:
  // blank means "show nothing" (compound). Only a notification from before the
  // field existed falls back to the number with the series unit.
  if ('peak_display' in params || 'peak_display_i18n' in params) {
    if (_nonBlank(params.peak_display)) params.peak = params.peak_display.trim();
  } else if (typeof params.peak_value === 'number' && Number.isFinite(params.peak_value)) {
    const value = formatNumber(params.peak_value, locale, { maximumFractionDigits: 4 });
    params.peak = _nonBlank(params.unit) ? `${value} ${params.unit.trim()}` : value;
  }
  // WHAT is back to normal; it ends a sentence of ours, so without its own full stop.
  if (_nonBlank(params.condition_sentence)) {
    params.condition = params.condition_sentence.trim().replace(/\.+$/, '');
  }
  const dateTime = (value) => formatDate(value, locale, { dateStyle: 'medium', timeStyle: 'short' });
  const since = dateTime(params.since);
  if (_nonBlank(since)) params.since_display = since;
  // A reminder: when the running episode began.
  const episodeSince = dateTime(params.episode_started_at);
  if (_nonBlank(episodeSince)) params.episode_since = episodeSince;
}

// Which message keys make up an alert notification. `body` may be several
// sentences (each its own key, joined by a space); a key that does not exist in
// the bundle is skipped, and the plain `title` / `body` is always the fallback.
const ALERT_VARIANTS = {
  'alert.triggered': {
    title: (p) => [p.is_test_notification ? 'title_test' : p.is_reminder ? 'title_reminder' : 'title'],
    // A reminder first says that it is one ("still active, since …").
    body: (p) => [
      p.is_reminder && (p.episode_since ? 'body_reminder_since' : 'body_reminder'),
      p.presented ? 'body_presented' : 'body',
    ],
  },
  'alert.cleared': {
    body: (p) => [
      p.condition ? 'body_condition' : 'body',
      p.duration && 'body_duration',
      p.peak && 'body_peak',
    ],
  },
  'alert.unhealthy': {
    body: (p) => [_nonBlank(p.dataset_label) ? 'body_dataset' : 'body', p.since_display && 'body_since'],
  },
  'alert.recovered': {
    body: (p) => [_nonBlank(p.dataset_label) ? 'body_dataset' : 'body', p.since_display && 'body_since'],
  },
};

/**
 * @param {Object|Function} notification — a notification doc, or a getter returning one
 * @returns {{ title: ComputedRef<string>, body: ComputedRef<string>, ctaLabel: ComputedRef<string|null>, ctaUrl: ComputedRef<string|null>, relativeTime: ComputedRef<string> }}
 */
export function useNotificationStrings(notification, suppliedI18n) {
  const i18n = suppliedI18n || useI18n();
  const t = i18n.t;
  const te = _safeTe(i18n);
  const getNotification = _toRef(notification);

  function _key(suffix) {
    const n = getNotification();
    if (!n || !n.notification_type) return null;
    return `notifications.${n.notification_type}.${suffix}`;
  }

  function _renderParams() {
    const n = getNotification() || {};
    const params = { ...(n.params || {}) };
    const locale = i18n.locale.value;
    _applyLocalizedParams(params, locale);
    if (ALERT_VARIANTS[n.notification_type]) _applyAlertParams(params, locale);
    // Pre-format common numeric / duration / date fields so templates don't need to call helpers.
    if (params.duration_seconds != null) {
      params.duration = formatDuration(params.duration_seconds, locale);
    }
    if (params.pnl_eur != null) {
      params.pnl = formatNumber(params.pnl_eur, locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
      });
    }
    if (params.current_value != null) {
      params.current_value = formatNumber(params.current_value, locale, {
        maximumFractionDigits: 4,
      });
    }
    if (params.threshold_value != null) {
      params.threshold_value = formatNumber(params.threshold_value, locale, {
        maximumFractionDigits: 4,
      });
    }
    // Benchmark profit fields: single value + group min/max range, as whole euros.
    const _eur = (value) =>
      formatNumber(value, locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      });
    if (params.profit_eur != null) {
      params.profit = _eur(params.profit_eur);
    } else if (params.profit == null) {
      const profitUnknownKey = `notifications.${n.notification_type}.profit_unknown`;
      if (te(profitUnknownKey)) {
        params.profit = t(profitUnknownKey);
      }
    }
    if (params.profit_low_eur != null) {
      params.profit_low = _eur(params.profit_low_eur);
    }
    if (params.profit_high_eur != null) {
      params.profit_high = _eur(params.profit_high_eur);
    }
    // Realized-period: combine the numeric magnitude with a pluralised unit word.
    if (params.period_unit && params.period_value != null) {
      const unitKey = `notifications.${n.notification_type}.period_units.${params.period_unit}`;
      let unitWord = params.period_unit;
      if (te(unitKey)) {
        unitWord = t(unitKey, {}, { plural: params.period_value });
      }
      params.period = `${formatNumber(params.period_value, locale)} ${unitWord}`;
    } else {
      const periodSelectedKey = `notifications.${n.notification_type}.period_selected`;
      if (te(periodSelectedKey)) {
        params.period = t(periodSelectedKey);
      }
    }
    if (params.settlement_date != null) {
      params.settlement_date_human = params.settlement_date;
    }
    // Benchmark run label: append the distinguishing characteristic (year for
    // forward benchmark, mode for imbalance benchmark) after the run name.
    // Produces e.g. " (jaar 2024)" or " (modus 1)"; empty string otherwise.
    if (params.scenario_year != null) {
      const yearKey = `notifications.${(getNotification() || {}).notification_type}.run_label_year`;
      params.run_label = te(yearKey) ? t(yearKey, params) : ` (jaar ${params.scenario_year})`;
    } else if (params.calculation_mode != null) {
      const modeKey = `notifications.${(getNotification() || {}).notification_type}.run_label_mode`;
      params.run_label = te(modeKey) ? t(modeKey, params) : ` (modus ${params.calculation_mode})`;
    } else {
      params.run_label = '';
    }
    return params;
  }

  function _renderWithPlural(suffix) {
    const params = _renderParams();
    const variants = ALERT_VARIANTS[(getNotification() || {}).notification_type]?.[suffix];
    if (variants) {
      const parts = variants(params)
        .filter(Boolean)
        .map((variantSuffix) => _key(variantSuffix))
        .filter((variantKey) => variantKey && te(variantKey))
        .map((variantKey) => t(variantKey, params));
      if (parts.length) return parts.join(' ');
      // An older bundle without the variant keys: the plain key below.
    }
    // Grouped benchmark runs use a dedicated body that renders a profit range.
    let resolvedSuffix = suffix;
    if (
      suffix === 'body' &&
      params.profit_low_eur != null &&
      params.profit_high_eur != null &&
      params.profit_low_eur !== params.profit_high_eur
    ) {
      const groupedKey = _key('body_grouped');
      if (groupedKey && te(groupedKey)) {
        resolvedSuffix = 'body_grouped';
      }
    }
    const key = _key(resolvedSuffix);
    if (!key || !te(key)) {
      // Graceful fallback: never throw on unknown templates.
      const n = getNotification() || {};
      if (suffix === 'title') return n.notification_type || '';
      return '';
    }
    // Pluralisation driver: prefer explicit `count`, otherwise fall back to a
    // small list of known per-template numeric fields. Adding a new template
    // with its own driver name only needs an entry here.
    const PLURAL_FIELDS = ['count', 'trades_count', 'new_count', 'resolved_count'];
    let pluralCount = null;
    for (const field of PLURAL_FIELDS) {
      if (typeof params[field] === 'number') {
        pluralCount = params[field];
        break;
      }
    }
    if (pluralCount != null) {
      // vue-i18n v9: pass params as named + plural via options. This signature
      // works for both pluralised messages ("singular | plural") and plain ones
      // without dropping named substitutions in the non-plural case.
      return t(key, params, { plural: pluralCount });
    }
    return t(key, params);
  }

  const title = computed(() => _renderWithPlural('title'));
  const body = computed(() => _renderWithPlural('body'));
  const ctaLabel = computed(() => {
    const key = _key('cta_label');
    if (!key || !te(key)) return null;
    return t(key, _renderParams());
  });
  const ctaUrl = computed(() => {
    const n = getNotification() || {};
    return normalizeNotificationRouteTarget(n.deep_link || (n.params && n.params.cta_url));
  });
  const relativeTime = computed(() => {
    const n = getNotification();
    if (!n || !n.created_at) return '';
    return formatRelativeTime(n.created_at, i18n.locale.value);
  });

  return { title, body, ctaLabel, ctaUrl, relativeTime };
}
